import { SearchAddressReverseResponse } from 'azure-maps-rest';

import {
  AddressResponse,
  getAzureMapsToken,
  getReverseSearchUrl,
  UserLocationProps,
} from '@xsf/shared-lib/hooks/location';

import { TypeaheadItem, TypeaheadSelectable } from '../typeahead/typeahead-new.types';

export const processTypeaheadData = (response: AddressResponse | undefined): TypeaheadItem[] => {
  const items = (response?.results.map((result) => {
    const { address, position } = result;
    const { lat, lon } = position;
    const { freeformAddress, municipality, countrySubdivision, postalCode } = address;

    const resultData: UserLocationProps & { id: string } = {
      id: result.id,
      address: freeformAddress,
      city: municipality,
      state: countrySubdivision,
      zipCode: postalCode,
      latitude: lat.toString(),
      longitude: lon.toString(),
    };

    return userLocationToTypeaheadItem(resultData);
  }) || []) as TypeaheadSelectable[];

  const seen = new Set<string>();

  // get rid of duplicate items in the results based on displayedValue
  const dedupItems = items.filter((item) => {
    if (item.displayedValue) {
      if (seen.has(item.displayedValue)) {
        return false;
      }
      seen.add(item.displayedValue);
      return true;
    }
    return true;
  });

  return dedupItems;
};

export const userLocationToTypeaheadItem = (
  userLocation: UserLocationProps & { id?: string }
): TypeaheadItem => {
  const { id, city, state, zipCode } = userLocation;

  // Generate a fallback ID if none is provided
  const fallbackId = Math.random().toString(36).substring(2, 9);

  const typeaheadItem: TypeaheadItem = {
    id: id || fallbackId,
    type: 'item',
    data: userLocation,
    displayedValue: `${city}, ${state}${zipCode ? ' ' + zipCode : ''}`,
    value: zipCode || id || fallbackId,
  };

  return typeaheadItem;
};

export const ensureZipCode = async (item: TypeaheadItem | null): Promise<TypeaheadItem | null> => {
  if (!item) return null;

  if (item.type === 'header') return item;

  const typedItem = item as TypeaheadSelectable;

  if (typedItem?.data && !typedItem.data?.zipCode) {
    const url = getReverseSearchUrl(
      typedItem.data?.latitude as string,
      typedItem.data?.longitude as string
    );

    if (!url) return null;
    const token = await getAzureMapsToken();
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-ms-client-id': process.env.NEXT_PUBLIC_AZURE_MAPS_CLIENT_ID || '',
      },
    });

    if (!response.ok) return null;

    const reverseSearchData: SearchAddressReverseResponse = await response.json();

    if (reverseSearchData?.addresses && reverseSearchData.addresses[0]?.address?.postalCode) {
      typedItem.value = reverseSearchData.addresses[0].address.postalCode;
      typedItem.data.zipCode = reverseSearchData.addresses[0].address.postalCode;
    }
  }

  return item;
};
